import * as E from './errors';
export type TranslationMapping = { [ languageId : string ] : string };
export const store                = new Map<string, TranslationMapping>();
export const computedTranslations = new Map;
export let languages       : string[] = [];
export let defaultLanguage : string;
export let language        : string;
export let isInitialized   : boolean = false;
export interface ITranslationsManagerDescriptor {
	languages       : string[];
	defaultLanguage : string;
	translations    : {
		[ translationId : string ] : TranslationMapping;
	};
};
export function addObject(
	translationId      : string,
	translationMapping : TranslationMapping
) {
	if (!isInitialized) {
		throw E.user('TranslationsManager is not initialized!');
	}
	for (const language of <string[]>languages) {
		if (typeof translationMapping[language] !== 'string') {
			throw E.user(
				(
					`Translation<${ translationId }> doens't implement ` +
					`language<${ language }> or the translation is not a string!`
				),
				{
					translationId,
					translationMapping,
					language
				}
			);
		}
	}
	store.set(translationId, translationMapping);
};
// addXML()
// exemple of a source node outerHTML:
// <myMapping trId='hello'>
// 	<myTranslation language='en'>Hello</myTranslation>
// 	<myTranslation language='fr'>Bonjour</myTranslation>
// 	<myTranslation language='es'>Hola</myTranslation>
// </myMapping>
// example of the schema to be used in adequation of source node:
// {
// 	translationId : 'trId',
// 	languageId    : 'language'
// }
export function addXML(
	sourceNode : HTMLElement,
	schema     : {
		translationId : string;
		languageId    : string;
	}
) {
	if (!isInitialized) {
		throw E.user('TranslationsManager is not initialized!');
	}
	const translationId = sourceNode.getAttribute(schema.translationId);
	if (!translationId) {
		throw E.user(
			"Source node doesn't defines translationId attribute!",
			{ sourceNode }
		);
	}
	const trMapping = Array.from(sourceNode.children).reduce((trMapping, translationNode) => {
		const languageId  = translationNode.getAttribute(schema.languageId);
		const translation = translationNode.textContent;
		if (!languageId) {
			throw E.user(
				(
					`Translation node of the source node<${ translationId }> ` +
					"doesn't defines languageId attribute!"
				),
				{
					translationId,
					sourceNode,
					translationNode
				}
			);
		}
		// normally translation should be checked and
		// throw if null but check is left to addObject
		trMapping[ languageId ] = <string>translation;
		return (trMapping);
	}, <TranslationMapping>{});
	addObject(translationId, trMapping);
};
export function update(node : Element | Document) {
	if (!isInitialized) {
		throw E.user('TranslationsManager is not initialized!');
	}
	if (!language) {
		throw E.user('No language currently in uses!');
	}
	const toTranslate     = node.querySelectorAll('[data-translation]');
	const toTranslateAttr = node.querySelectorAll('[data-translation-attr]');
	(<HTMLElement[]><any>Array.from(toTranslate)).forEach(translateNodeText);
	(<HTMLElement[]><any>Array.from(toTranslateAttr)).forEach(translateNodeAttr);
};
export function use(languageId : string) {
	if (!isInitialized) {
		throw E.user('TranslationsManager is not initialized!');
	}
	language = languages.includes(languageId) ? languageId : defaultLanguage;
	update(document);
};
export function get(translationId : string, languageId : string) {
	if (!isInitialized) {
		throw E.user('TranslationsManager is not initialized!');
	}
	const translations = store.get(translationId);
	if (!translations) {
		throw E.user(
			`Unknown translation id<${ translationId }>!`,
			{ translationId }
		);
	}
	const translation = translations[ languageId ];
	//In correct cases translations are checked by the "adding" method so
	//check that the errored translation was added by the correct add method
	//(Sure that was not)
	if (typeof translation !== 'string') {
		throw E.library(
			'Unknown error!',
			{
				translationId,
				languageId,
				realMessage : (
					'Translation mapping ' +
					"doesn\'t define translation for the selected language"
				)
			}
		);
	}
	return (translation);
};
export function initialize(descriptor : ITranslationsManagerDescriptor) {
	if (isInitialized) {
		throw E.user('TranslationsManager is already initialized!');
	}
	languages       = descriptor.languages;
	defaultLanguage = descriptor.defaultLanguage;
	language        = defaultLanguage;
	isInitialized   = true;
	if (descriptor.translations) {
		for (const [ id, mapping ] of Object.entries(descriptor.translations)) {
			addObject(id, mapping);
		}
	}
};
const translateNodeText = (node : HTMLElement) => {
	if (!isInitialized) {
		throw E.user('TranslationsManager is not initialized!');
	}
	try {
		node.textContent = get(
			<string>node.dataset.translation,
			<string>language
		);
	} catch (e : unknown) {
		throw E.bindFromUnknown('Cannot translate node text!', { node }, e);
	}
};
const translateNodeAttr = (node : HTMLElement) => {
	if (!isInitialized) {
		throw E.user('TranslationsManager is not initialized!');
	}
	const [ attribute, trId ] = (<string>node.dataset.translationAttr).split(':');
	if (!attribute || !trId) {
		throw E.user('Invalid translatable attribute', { node, attribute, trId });
	}
	node.setAttribute(
		attribute,
		get(trId, <string>language)
	);
};
